import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  LayoutDashboard,
  FileText,
  Settings2,
  Users,
  ImageIcon,
  LogOut,
  ExternalLink,
  Menu,
  X,
} from "lucide-react";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/articles", label: "Articles", icon: FileText },
  { to: "/admin/content", label: "Site content", icon: Settings2 },
  { to: "/admin/subscribers", label: "Subscribers", icon: Users },
  { to: "/admin/media", label: "Media", icon: ImageIcon },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-secondary/60 border-r border-border/60 z-40 transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="h-full flex flex-col p-6">
          <div className="flex items-center justify-between mb-12">
            <Link to="/admin" className="font-serif text-2xl">
              GentleGi<span className="italic text-primary">T</span>o
            </Link>
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          <nav className="flex-1 space-y-1">
            {navItems.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                    isActive
                      ? "bg-background text-foreground"
                      : "text-foreground/70 hover:bg-background/60 hover:text-foreground"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.5} />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-8 pt-6 border-t border-border/60 space-y-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-xs tracking-wide text-muted-foreground hover:text-primary"
            >
              <ExternalLink size={13} strokeWidth={1.5} />
              View site
            </a>
            <p className="text-xs text-muted-foreground truncate" title={admin?.email}>
              {admin?.email}
            </p>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs tracking-wide text-foreground/70 hover:text-destructive"
            >
              <LogOut size={13} strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-md border-b border-border/60 lg:hidden">
          <div className="flex items-center justify-between px-6 h-16">
            <Link to="/admin" className="font-serif text-xl">
              GentleGi<span className="italic text-primary">T</span>o
            </Link>
            <button
              data-testid="admin-mobile-menu"
              onClick={() => setMobileOpen(true)}
              className="p-2"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>
        </header>

        <main className="p-6 sm:p-10 lg:p-14 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
